import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { StoreSelectorService } from 'src/app/services/storeselector.service';
import { StoreSelection, Store } from 'src/app/models/store';
import { StoreHoursService } from 'src/app/services/storehours.service';
import { StoreHours } from 'src/app/models/storeHours';
import { map } from 'rxjs/operators';

export interface HoursForWeek {
  dayOfWeek: string;
  effectiveHours: number[];
}

const HOURS_IN_DAY_NAMES: string[] = ['12am','1am','2am','3am','4am','5am','6am','7am','8am','9am','10am','11am','12pm','1pm','2pm','3pm','4pm','5pm','6pm','7pm','8pm','9pm','10pm','11pm'];
const HOURS_IN_DAY: string[] = ['0','1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18','19','20','21','22','23'];
const DAYS_OF_WEEK: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_DATA: HoursForWeek[] = [
  {dayOfWeek: 'Sunday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]},
  {dayOfWeek: 'Monday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]},
  {dayOfWeek: 'Tuesday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]},
  {dayOfWeek: 'Wednesday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]},
  {dayOfWeek: 'Thursday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]},
  {dayOfWeek: 'Friday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]},
  {dayOfWeek: 'Saturday', effectiveHours: [0,0,0,0,0,0,0,0,0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0]}
];

@Component({
  selector: 'app-store-hours',
  templateUrl: './store-hours.component.html',
  styleUrls: ['./store-hours.component.scss']
})

export class StoreHoursComponent implements OnInit, OnDestroy {

  hoursInDayNames = HOURS_IN_DAY_NAMES;
  hoursInDay = HOURS_IN_DAY;
  displayedColumns: string[] = ['dayOfWeek'].concat(HOURS_IN_DAY);
  dataSource = this.buildStoreHours(null);
  showLoading: boolean = true;

  private selectedStoreChangesSubscription: Subscription;
  selectedStore: StoreSelection;
  
  constructor(private _storeSelectorService: StoreSelectorService, private _storeHoursService: StoreHoursService) { }

  ngOnInit() {
    /* Selected Store */
    // Get initial selected store
    let initSelectedStore: StoreSelection = this._storeSelectorService.getSelectedStore();
    this.onStoreChange(initSelectedStore);

    // Update Selected Store UI when selected store changes
    this.selectedStoreChangesSubscription = this._storeSelectorService.storeChanges.subscribe((newSelectedStore: StoreSelection) => {
      this.onStoreChange(newSelectedStore);
    });
  }

  private onStoreChange(newSelectedStore: StoreSelection) {
    this.selectedStore = newSelectedStore;
    this.refresh();
  }

  ngOnDestroy() {
	  this.selectedStoreChangesSubscription.unsubscribe();
  }

  // Returns true if valid, false if invalid
  private checkStoreIsValid(): boolean {
    if (this.selectedStore === null) {
      return false;
    }
    return true;
  }

  refresh() {
    this.getStoreHours();
  }

  getStoreHours() {

    this.showLoading = true;
    if(!this.checkStoreIsValid()) {
      return;
    }
    
    var data = this._storeHoursService.getStoreHours(this.selectedStore.storeGuid).pipe(
      map(storeHours => {
        if (storeHours != null && storeHours[storeHours.length-1].expiryDate == null) {
          this.dataSource = this.buildStoreHours(this._storeHoursService.base64ToBytes(storeHours[storeHours.length-1].effectiveHours));
        } else {
          this.dataSource = this.buildStoreHours(null);
        }
      })
    )

    data.subscribe(() => {
      this.showLoading = false;
    }, (error: any) => {
      console.error(error);
      this.showLoading = false;
    });

  }

  createDefaults() {
    var copy = [];
    DEFAULT_DATA.forEach((d) => {
      copy.push({ dayOfWeek: d.dayOfWeek, effectiveHours: d.effectiveHours.slice() });
    })
    this.dataSource = copy;
  }

  clearSchedule() {
    this.dataSource = this.buildStoreHours(null);
  }

  buildStoreHours(data:any) {
    var schedule = [];
    for(var day of DAYS_OF_WEEK) {
      var forDay = [];
      for (var hour of HOURS_IN_DAY) {
        var index = schedule.length * HOURS_IN_DAY.length + forDay.length
        forDay.push(data == null ? 0 : data[schedule.length * HOURS_IN_DAY.length + forDay.length]);
      }
      schedule.push({'dayOfWeek': day, 'effectiveHours': forDay });
    }

    return schedule;
  }

  updateStoreHours() {
    this.showLoading = true;
    let hours: number[] = [];
    this.dataSource.forEach(d => hours = hours.concat(d.effectiveHours));

    const hoursArray = Uint8Array.from(hours);

    this._storeHoursService.addStoreHours(this.selectedStore.storeGuid, hoursArray).subscribe(() => {
      this.showLoading = false;
    }, (error: any) => {
      console.error(error);
      this.showLoading = false;
    });
  }

  onHoursChanged(element: HoursForWeek, hour: number, event: any) {
    console.log(`${element.dayOfWeek} hour ${hour} changed to ${event.checked}`)

    element.effectiveHours[hour] = event.checked ? 1 : 0;
  }

  isAllSelected(hour) {
    let selected = this.dataSource.filter(s => s.effectiveHours[hour]).length;
    return selected == DAYS_OF_WEEK.length;
  }

  isAnySelected(hour) {
    let selected = this.dataSource.filter(s => s.effectiveHours[hour]).length;
    return selected > 0 && selected != DAYS_OF_WEEK.length;
  }

  toggleHour(hour, target) {
    target.preventDefault();
    var selection = this.dataSource.filter(s => s.effectiveHours[hour]);
    if (selection.length > 0) {
      selection.forEach(element => {
        element.effectiveHours[hour] = 0;
      });
    } else if (selection.length == 0) {
      selection = this.dataSource.filter(s => s.effectiveHours[hour] == 0);
      selection.forEach(element => {
        element.effectiveHours[hour] = 1;
      });
    }
  }

}
